import type { ApolloClient } from '@apollo/client';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import { FETCH_PORTFOLIO_REPORTS_BY_OPTIMIZER_RUN } from '../../queries/optimizerRunReports.queries';
import { REFRESH_PORTFOLIO_REPORT_FOR_LOOKUP } from '../../queries/portfolioReportLookup.queries';

const ORG_SCAN_PAGE_SIZE = 100;
const ORG_SCAN_MAX_REPORTS = 500;
const OPTIMIZER_RUN_FETCH_MAX = 500;
// Each refresh is a mutation; cap how many run at once so a run with many
// missing reports doesn't fan out into hundreds of concurrent recomputes.
const REFRESH_CONCURRENCY = 10;

type PortfolioReportFilter = {
  organizationId: string;
  optimizerRunId?: string;
};

const fetchReports = async (
  client: ApolloClient<unknown>,
  filter: PortfolioReportFilter,
  perPage: number,
  offSet = 0,
): Promise<PortfolioReport[]> => {
  try {
    const result = await client.query({
      query: FETCH_PORTFOLIO_REPORTS_BY_OPTIMIZER_RUN,
      variables: {
        skipErrorHandler: true,
        input: {
          filter,
          pagination: {
            perPage,
            offSet,
          },
        },
      },
      fetchPolicy: 'network-only',
    });

    return (result.data?.fetchPortfolioReports?.portfolioReports ??
      []) as PortfolioReport[];
  } catch {
    return [];
  }
};

const fetchReportsForOptimizerRun = async (
  client: ApolloClient<unknown>,
  organizationId: string,
  optimizerRunId: string,
  targetIds: Set<string>,
): Promise<Map<string, PortfolioReport>> => {
  const resolved = new Map<string, PortfolioReport>();
  const reports = await fetchReports(
    client,
    { organizationId, optimizerRunId },
    OPTIMIZER_RUN_FETCH_MAX,
  );

  for (const report of reports) {
    if (targetIds.has(report.id)) {
      resolved.set(report.id, report);
    }
  }

  return resolved;
};

const scanOrganizationReports = async (
  client: ApolloClient<unknown>,
  organizationId: string,
  targetIds: Set<string>,
): Promise<Map<string, PortfolioReport>> => {
  const resolved = new Map<string, PortfolioReport>();
  let offset = 0;

  while (resolved.size < targetIds.size && offset < ORG_SCAN_MAX_REPORTS) {
    const reports = await fetchReports(
      client,
      { organizationId },
      ORG_SCAN_PAGE_SIZE,
      offset,
    );

    if (reports.length === 0) {
      break;
    }

    for (const report of reports) {
      if (targetIds.has(report.id)) {
        resolved.set(report.id, report);
      }
    }

    offset += reports.length;

    if (reports.length < ORG_SCAN_PAGE_SIZE) {
      break;
    }
  }

  return resolved;
};

const refreshPortfolioReportById = async (
  client: ApolloClient<unknown>,
  reportId: string,
): Promise<PortfolioReport | null> => {
  try {
    const result = await client.mutate({
      mutation: REFRESH_PORTFOLIO_REPORT_FOR_LOOKUP,
      variables: {
        skipErrorHandler: true,
        input: {
          portfolioReportId: reportId,
        },
      },
    });

    return (result.data?.refreshPortfolioReport?.portfolioReport ??
      null) as PortfolioReport | null;
  } catch {
    return null;
  }
};

const refreshPortfolioReportsByIds = async (
  client: ApolloClient<unknown>,
  reportIds: string[],
): Promise<Map<string, PortfolioReport>> => {
  const resolved = new Map<string, PortfolioReport>();

  for (
    let offset = 0;
    offset < reportIds.length;
    offset += REFRESH_CONCURRENCY
  ) {
    const chunk = reportIds.slice(offset, offset + REFRESH_CONCURRENCY);
    const reports = await Promise.all(
      chunk.map((reportId) => refreshPortfolioReportById(client, reportId)),
    );

    for (const report of reports) {
      if (report?.id) {
        resolved.set(report.id, report);
      }
    }
  }

  return resolved;
};

export type ResolvePortfolioReportsByIdsOptions = {
  /** When set, try run-scoped fetch before org scan (supported HS filter). */
  optimizerRunId?: string;
  /**
   * Caller already searched the run-scoped report list. Skip that refetch
   * and the org-wide scan; load the known IDs directly.
   */
  preferDirectRefresh?: boolean;
};

export const resolvePortfolioReportsByIds = async (
  client: ApolloClient<unknown>,
  organizationId: string,
  reportIds: string[],
  options: ResolvePortfolioReportsByIdsOptions = {},
): Promise<Map<string, PortfolioReport>> => {
  const uniqueReportIds = [...new Set(reportIds)];
  const targetIds = new Set(uniqueReportIds);
  const resolved = new Map<string, PortfolioReport>();

  if (options.optimizerRunId && !options.preferDirectRefresh) {
    const fromRun = await fetchReportsForOptimizerRun(
      client,
      organizationId,
      options.optimizerRunId,
      targetIds,
    );

    for (const [id, report] of fromRun.entries()) {
      resolved.set(id, report);
    }
  }

  let unresolvedIds = uniqueReportIds.filter((id) => !resolved.has(id));

  if (unresolvedIds.length > 0 && !options.preferDirectRefresh) {
    const scanned = await scanOrganizationReports(
      client,
      organizationId,
      new Set(unresolvedIds),
    );

    for (const [id, report] of scanned.entries()) {
      resolved.set(id, report);
    }
  }

  unresolvedIds = uniqueReportIds.filter((id) => !resolved.has(id));

  if (unresolvedIds.length > 0) {
    const refreshed = await refreshPortfolioReportsByIds(client, unresolvedIds);

    for (const [id, report] of refreshed.entries()) {
      resolved.set(id, report);
    }
  }

  return resolved;
};

/** @internal Exposed for tests. */
export const refreshPortfolioReportByIdForLookup = refreshPortfolioReportById;
